/*
 * Copyright (C) [2025] [Netzint GmbH]
 * All rights reserved.
 *
 * This software is dual-licensed under the terms of:
 *
 * 1. The GNU Affero General Public License (AGPL-3.0-or-later), as published by the Free Software Foundation.
 *    You may use, modify and distribute this software under the terms of the AGPL, provided that you comply with its conditions.
 *
 *    A copy of the license can be found at: https://www.gnu.org/licenses/agpl-3.0.html
 *
 * OR
 *
 * 2. A commercial license agreement with Netzint GmbH. Licensees holding a valid commercial license from Netzint GmbH
 *    may use this software in accordance with the terms contained in such written agreement, without the obligations imposed by the AGPL.
 *
 * If you are uncertain which license applies to your use case, please contact us at info@netzint.de for clarification.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';
import { Input, inputVariants } from './Input';

type ActionIcon = { icon: IconDefinition; onClick: () => void; className?: string };

type InputWithActionIconsProps = React.InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof inputVariants> & { actionIcons?: ActionIcon[] };

const InputWithActionIcons = React.forwardRef<HTMLInputElement, InputWithActionIconsProps>(
  ({ actionIcons = [], className, variant, disabled, readOnly, style, ...props }, ref) => {
    const iconCount = actionIcons.length;
    const paddingRight = iconCount > 0 ? iconCount * 24 + 8 : undefined;

    return (
      <div className={cn('relative w-full', className)}>
        <Input
          {...props}
          ref={ref}
          variant={variant}
          className={cn('overflow-hidden text-ellipsis whitespace-nowrap', {
            'cursor-pointer': props.onMouseDown,
          })}
          style={{ ...style, paddingRight }}
          readOnly={readOnly}
          disabled={disabled}
        />
        {iconCount > 0 && (
          <div className="absolute inset-y-0 right-0 flex items-center space-x-2 pr-2">
            {actionIcons.map(({ icon, onClick, className: btnClass }) => (
              <button
                key={icon.iconName}
                type="button"
                onClick={onClick}
                disabled={disabled}
                className="flex items-center justify-center hover:opacity-60"
              >
                <FontAwesomeIcon
                  icon={icon}
                  className={cn('h-4 w-4 cursor-pointer', disabled && 'text-muted', btnClass)}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

InputWithActionIcons.displayName = 'InputWithActionIcons';

export default InputWithActionIcons;
export type { InputWithActionIconsProps, ActionIcon };
